import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toDateString } from '@/lib/utils/dateUtils';
import {
  AccrualTransaction,
  AccrualSummary,
  AccrualByCategory,
  AccrualStatus,
  formatCurrency
} from '../../types/accrual';

interface AccrualChartProps {
  data: AccrualTransaction[];
  summary: AccrualSummary;
  categoryData: AccrualByCategory[];
}

const COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', 
  '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1'
];

export function AccrualChart({ data, summary, categoryData }: AccrualChartProps) {
  
  // Prepare revenue vs expenses data
  const revenueExpensesData = [
    {
      name: 'Previsto',
      receitas: summary.total_revenue,
      despesas: summary.total_expenses,
      resultado: summary.net_result
    },
    {
      name: 'Realizado',
      receitas: summary.paid_revenue,
      despesas: summary.paid_expenses,
      resultado: summary.paid_revenue - summary.paid_expenses
    },
    {
      name: 'Pendente',
      receitas: summary.pending_revenue,
      despesas: summary.pending_expenses,
      resultado: summary.pending_revenue - summary.pending_expenses
    }
  ];

  // Prepare category distribution data
  const categoryChartData = categoryData.map((category, index) => ({
    ...category,
    color: COLORS[index % COLORS.length]
  }));

  // Prepare status distribution data - using mock data since by_status is not in AccrualSummary
  const statusData = [
    { name: 'Pago', value: summary.paid_revenue + summary.paid_expenses, color: '#10b981' },
    { name: 'Pendente', value: summary.pending_revenue + summary.pending_expenses, color: '#f59e0b' },
    { name: 'Em Atraso', value: summary.overdue_revenue + summary.overdue_expenses, color: '#ef4444' }
  ].filter(item => item.value > 0);

  // Prepare daily trend data (group by day)
  const dailyTrend = React.useMemo(() => {
    const dailyMap = new Map<string, { receitas: number; despesas: number; date: Date }>();

    data.forEach(transaction => {
      const dateKey = toDateString(transaction.competence_date);
      const existing = dailyMap.get(dateKey) || { receitas: 0, despesas: 0, date: new Date(transaction.competence_date) };
      
      if (transaction.type === 'receita') {
        existing.receitas += transaction.amount;
      } else {
        existing.despesas += Math.abs(transaction.amount);
      }
      
      dailyMap.set(dateKey, existing);
    });

    return Array.from(dailyMap.entries())
      .map(([date, values]) => ({
        date,
        day: new Date(date).getDate(),
        receitas: values.receitas,
        despesas: values.despesas,
        resultado: values.receitas - values.despesas
      }))
      .sort((a, b) => a.day - b.day);
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background/95 backdrop-blur-sm border border-border/50 rounded-xl shadow-2xl p-5 min-w-64 animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
            <p className="font-semibold text-foreground">{label}</p>
          </div>
          <div className="space-y-2">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex justify-between items-center p-2 rounded-lg bg-muted/50 border border-border/30">
                <span className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full shadow-lg"
                    style={{ 
                      background: `linear-gradient(135deg, ${entry.color}, ${entry.color}dd)`,
                      boxShadow: `0 0 8px ${entry.color}40`
                    }}
                  ></div>
                  <span className="font-medium text-sm">{entry.name}</span>
                </span>
                <span className="font-bold" style={{ color: entry.color }}>
                  {formatCurrency(entry.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background/95 backdrop-blur-sm border border-border/50 rounded-xl shadow-2xl p-5 min-w-64 animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="flex items-center gap-2 mb-3">
            <div 
              className="w-3 h-3 rounded-full shadow-lg animate-pulse"
              style={{ 
                background: `linear-gradient(135deg, ${data.color || payload[0].color}, ${data.color || payload[0].color}dd)`,
                boxShadow: `0 0 8px ${data.color || payload[0].color}40`
              }}
            ></div>
            <p className="font-semibold text-foreground">{data.category || data.name}</p>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50 border border-border/30">
              <span className="text-sm font-medium">Valor</span>
              <span className="font-bold text-primary">{formatCurrency(data.total_amount || data.value)}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50 border border-border/30">
              <span className="text-sm font-medium">Percentual</span>
              <span className="font-bold text-primary">{(data.percentage || ((data.value / statusData.reduce((sum, item) => sum + item.value, 0)) * 100)).toFixed(1)}%</span>
            </div>
            {data.transactions_count && (
              <div className="flex justify-between items-center p-2 rounded-lg bg-muted/50 border border-border/30">
                <span className="text-sm font-medium">Transações</span>
                <span className="font-bold text-primary">{data.transactions_count}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="categories">Categorias</TabsTrigger>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="trend">Tendência</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Receitas vs Despesas</CardTitle>
              <CardDescription>
                Comparação entre valores previstos, realizados e pendentes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueExpensesData} margin={{ top: 30, right: 40, left: 20, bottom: 20 }}>
                    
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke="var(--border)" 
                      strokeOpacity={0.3}
                      horizontal={true}
                      vertical={false}
                    />
                    
                    <XAxis 
                      dataKey="name" 
                      stroke="var(--muted-foreground)"
                      fontSize={12}
                      fontWeight={500}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />
                    
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value)}
                      stroke="var(--muted-foreground)"
                      fontSize={11}
                      fontWeight={500}
                      tickLine={false}
                      axisLine={false}
                      dx={-10}
                    />
                    
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ 
                        fill: 'var(--muted)', 
                        fillOpacity: 0.1,
                        stroke: 'var(--border)',
                        strokeWidth: 1,
                        strokeDasharray: '5 5'
                      }}
                    />
                    
                    <Legend 
                      wrapperStyle={{ 
                        paddingTop: '25px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      iconType="rect"
                      iconSize={12}
                    />
                    
                    <Bar 
                      dataKey="receitas" 
                      fill="#10b981" 
                      name="Receitas" 
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar 
                      dataKey="despesas" 
                      fill="#ef4444" 
                      name="Despesas" 
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar 
                      dataKey="resultado" 
                      fill="#3b82f6" 
                      name="Resultado" 
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                
                {/* Modern overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/5 via-transparent to-background/5 pointer-events-none rounded-lg"></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Distribuição por Categoria</CardTitle>
                <CardDescription>
                  Participação de cada categoria no total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      
                      <Pie
                        data={categoryChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ category, percentage }) => percentage > 8 ? `${category} (${percentage.toFixed(1)}%)` : ''}
                        outerRadius={90}
                        innerRadius={20}
                        fill="#8884d8"
                        dataKey="total_amount"
                        paddingAngle={2}
                      >
                        {categoryChartData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
fill={entry.color}
                            stroke="rgba(255,255,255,0.8)"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Modern overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/5 via-transparent to-background/5 pointer-events-none rounded-lg"></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ranking de Categorias</CardTitle>
                <CardDescription>
                  Categorias ordenadas por valor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categoryChartData
                    .sort((a, b) => b.total_amount - a.total_amount)
                    .slice(0, 10)
                    .map((category, index) => (
                      <div key={category.category} className="group relative">
                        {/* Hover background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-muted/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                        
                        <div className="relative flex items-center justify-between p-3 rounded-lg border border-transparent group-hover:border-border/50 transition-all duration-300">
                          <div className="flex items-center space-x-3">
                            {/* Enhanced rank indicator */}
                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-muted to-muted/50 text-xs font-bold text-muted-foreground">
                              {index + 1}
                            </div>
                            
                            {/* Enhanced color indicator */}
                            <div 
                              className="w-4 h-4 rounded-full shadow-lg border-2 border-white dark:border-gray-800" 
                              style={{ 
                                background: `linear-gradient(135deg, ${category.color}, ${category.color}dd)`,
                                boxShadow: `0 0 12px ${category.color}40`
                              }}
                            />
                            
                            <span className="text-sm font-medium group-hover:font-semibold transition-all">
                              {category.category}
                            </span>
                            
                            <Badge variant="outline" className="text-xs bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                              {category.transactions_count} transações
                            </Badge>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-sm font-bold tracking-tight group-hover:text-primary transition-colors">
                              {formatCurrency(category.total_amount)}
                            </div>
                            <div className="text-xs font-medium text-muted-foreground">
                              {category.percentage.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="relative mt-1 h-1 bg-muted/30 rounded-full overflow-hidden">
                          <div 
                            className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r transition-all duration-700 ease-out"
                            style={{ 
                              width: `${category.percentage}%`,
                              background: `linear-gradient(90deg, ${category.color}aa, ${category.color})`,
                            }}
                          />
                        </div>
                      </div>
                    ))
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="status" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Status</CardTitle>
              <CardDescription>
                Status dos lançamentos do período
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="h-80 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                        outerRadius={85}
                        innerRadius={25}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={3}
                      >
                        {statusData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
fill={COLORS[index % COLORS.length]}
                            stroke="rgba(255,255,255,0.8)"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Modern overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/5 via-transparent to-background/5 pointer-events-none rounded-lg"></div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                    Resumo por Status
                  </h4>
                  <div className="space-y-3">
                    {statusData.map((item, index) => (
                      <div key={item.name} className="group relative">
                        {/* Enhanced hover background */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-muted/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                        
                        <div className="relative p-4 rounded-xl border border-border/50 bg-gradient-to-br from-card to-card/50 group-hover:shadow-lg transition-all duration-300">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {/* Enhanced status indicator */}
                              <div 
                                className="w-5 h-5 rounded-full shadow-lg border-2 border-white dark:border-gray-800" 
                                style={{ 
                                  background: `linear-gradient(135deg, ${COLORS[index % COLORS.length]}, ${COLORS[index % COLORS.length]}dd)`,
                                  boxShadow: `0 0 12px ${COLORS[index % COLORS.length]}40`
                                }}
                              />
                              <span className="font-semibold group-hover:text-primary transition-colors">
                                {item.name}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-lg tracking-tight group-hover:scale-105 transition-transform">
                                {formatCurrency(item.value)}
                              </div>
                              <div className="text-sm font-medium text-muted-foreground">
                                {item.count} lançamentos
                              </div>
                            </div>
                          </div>
                          
                          {/* Progress indicator */}
                          <div className="mt-3 h-1 bg-muted/30 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full bg-gradient-to-r transition-all duration-700 ease-out"
                              style={{ 
                                width: `${(item.value / Math.max(...statusData.map(s => s.value))) * 100}%`,
                                background: `linear-gradient(90deg, ${COLORS[index % COLORS.length]}aa, ${COLORS[index % COLORS.length]})`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tendência Diária</CardTitle>
              <CardDescription>
                Evolução de receitas e despesas ao longo do período
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyTrend} margin={{ top: 30, right: 40, left: 20, bottom: 20 }}>
                    
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke="var(--border)" 
                      strokeOpacity={0.3}
                      horizontal={true}
                      vertical={false}
                    />
                    
                    <XAxis 
                      dataKey="day" 
                      stroke="var(--muted-foreground)"
                      fontSize={11}
                      fontWeight={500}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />
                    
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value)}
                      stroke="var(--muted-foreground)"
                      fontSize={11}
                      fontWeight={500}
                      tickLine={false}
                      axisLine={false}
                      dx={-10}
                    />
                    
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ 
                        stroke: 'var(--border)',
                        strokeWidth: 1,
                        strokeDasharray: '5 5'
                      }}
                    />
                    
                    <Legend 
                      wrapperStyle={{ 
                        paddingTop: '25px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      iconType="rect"
                      iconSize={12}
                    />
                    
                    <Line 
                      type="monotone" 
                      dataKey="receitas" 
                      stroke="#10b981"
                      strokeWidth={3}
                      name="Receitas"
                      dot={{ 
                        fill: '#10b981', 
                        strokeWidth: 3, 
                        r: 4
                      }}
                      activeDot={{ 
                        r: 6, 
                        stroke: '#10b981',
                        strokeWidth: 3,
                        fill: '#ffffff'
                      }}
                    />
                    
                    <Line 
                      type="monotone" 
                      dataKey="despesas" 
                      stroke="#ef4444"
                      strokeWidth={3}
                      name="Despesas"
                      dot={{ 
                        fill: '#ef4444', 
                        strokeWidth: 3, 
                        r: 4
                      }}
                      activeDot={{ 
                        r: 6, 
                        stroke: '#ef4444',
                        strokeWidth: 3,
                        fill: '#ffffff'
                      }}
                    />
                    
                    <Line 
                      type="monotone" 
                      dataKey="resultado" 
                      stroke="#3b82f6"
                      strokeWidth={3}
                      name="Resultado"
                      dot={{ 
                        fill: '#3b82f6', 
                        strokeWidth: 3, 
                        r: 4
                      }}
                      activeDot={{ 
                        r: 6, 
                        stroke: '#3b82f6',
                        strokeWidth: 3,
                        fill: '#ffffff'
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                
                {/* Modern overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/5 via-transparent to-background/5 pointer-events-none rounded-lg"></div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardHeader className="relative pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  Melhor Dia
                </CardTitle>
              </CardHeader>
              <CardContent className="relative">
                {(() => {
                  const bestDay = dailyTrend.reduce((best, current) => 
                    current.resultado > best.resultado ? current : best
                  , dailyTrend[0] || { day: 1, resultado: 0 });
                  
                  return (
                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-green-600 tracking-tight group-hover:scale-105 transition-transform">
                        Dia {bestDay.day}
                      </div>
                      <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                        <div className="text-sm font-semibold text-green-700 dark:text-green-400">
                          {formatCurrency(bestDay.resultado)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card className="relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardHeader className="relative pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                  Pior Dia
                </CardTitle>
              </CardHeader>
              <CardContent className="relative">
                {(() => {
                  const worstDay = dailyTrend.reduce((worst, current) => 
                    current.resultado < worst.resultado ? current : worst
                  , dailyTrend[0] || { day: 1, resultado: 0 });
                  
                  return (
                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-red-600 tracking-tight group-hover:scale-105 transition-transform">
                        Dia {worstDay.day}
                      </div>
                      <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                        <div className="text-sm font-semibold text-red-700 dark:text-red-400">
                          {formatCurrency(worstDay.resultado)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card className="relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardHeader className="relative pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                  Média Diária
                </CardTitle>
              </CardHeader>
              <CardContent className="relative">
                {(() => {
                  const averageResult = dailyTrend.length > 0 
                    ? dailyTrend.reduce((sum, day) => sum + day.resultado, 0) / dailyTrend.length
                    : 0;
                  
                  return (
                    <div className="space-y-2">
                      <div className={`text-3xl font-bold tracking-tight group-hover:scale-105 transition-transform ${
                        averageResult >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(averageResult)}
                      </div>
                      <div className={`p-2 rounded-lg border ${
                        averageResult >= 0 
                          ? 'bg-green-500/10 border-green-500/20' 
                          : 'bg-red-500/10 border-red-500/20'
                      }`}>
                        <div className={`text-sm font-semibold ${
                          averageResult >= 0 
                            ? 'text-green-700 dark:text-green-400' 
                            : 'text-red-700 dark:text-red-400'
                        }`}>
                          Por dia
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}